import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Info, Target, Scales, ChartBar, TrendUp, ArrowsClockwise, NumberCircleOne, Lightning } from '@phosphor-icons/react';

const PERBALL_WEIGHTS = [
  {
    id: 'W1', name: 'Month Deficit', pct: '7% of 70%', color: 'from-slate-500 to-slate-700',
    principle: 'Compares a diff\'s overall frequency (%) against its frequency in the current calendar month (%). If overall% > month%, the diff is underrepresented this month — it\'s historically "overdue" for this month specifically.',
    formula: '((overall% - month%) / overall%) × 10 × overall_ratio',
    insight: 'A diff with 5% overall but only 2% in April has a 60% deficit — strong signal it\'s due in April.'
  },
  {
    id: 'W2', name: 'Absence in Current Month', pct: '7% of 70%', color: 'from-gray-500 to-gray-700',
    principle: 'Checks if this diff has appeared in the current month this year. If it hasn\'t appeared at all (0 times) despite having historical presence, it gets maximum boost. If below expected based on overall frequency, partial boost.',
    formula: 'If month_current_year=0: 10 × overall_ratio. Else: deficit_ratio × 5 × overall_ratio',
    insight: 'Rewards diffs that are "missing" from the current month — they\'re statistically due.'
  },
  {
    id: 'W3', name: 'Consecutive Absent Years', pct: '4%×mult of 70%', color: 'from-orange-500 to-orange-700',
    principle: 'Tracks how many consecutive recent years this diff was absent from the current month. Uses an accelerating progressive multiplier: each additional absent year increases pressure by +0.25× (1yr=1.0×, 2yr=1.25×, 3yr=1.5×, 4yr=1.75×).',
    formula: '(absent_years / max_absent) × 10 × overall_ratio × progressive_multiplier',
    insight: 'A diff absent from April for 3 straight years gets 1.5× the base weight — compounding pressure.'
  },
  {
    id: 'W4', name: 'Peer Lag', pct: '5% of 70%', color: 'from-teal-500 to-teal-700',
    principle: 'Finds "peer" diffs with similar overall frequency (±30%) and checks if this diff is lagging behind its peers in the current month. If peers have appeared more, this diff is comparatively overdue.',
    formula: '((max_peer_month - this_month) / (max_peer - min_peer)) × 10',
    insight: 'If +3 and -3 both appear ~4% overall but -3 has 5 month appearances vs +3\'s 2, then +3 is lagging.'
  },
  {
    id: 'W5', name: 'Yearly Performance Signal', pct: '9% of 70%', color: 'from-indigo-500 to-indigo-700',
    principle: 'BIDIRECTIONAL: Compares actual appearances this year against the average of the last 3 complete years. Underperforming → positive boost (due to appear more). Overperforming → negative penalty (already exhausted yearly quota).',
    formula: 'usage_ratio = actual_this_year / expected_full_year. <0.3: +8, <0.5: +3, >0.8: -2, >1.0: -10',
    insight: 'If +5 averaged 8×/year over 3 years but only appeared 2× so far this year (usage=0.25), it gets a strong boost.'
  },
  {
    id: 'W6', name: 'Lifetime Month Absence', pct: '20% of 70%', color: 'from-red-500 to-red-700',
    principle: 'Highest-weighted W1-W9 factor. Diffs that have NEVER appeared (or very rarely) in this specific calendar month across all historical years get massive boost. Tracks the fraction of years this diff was absent from this month.',
    formula: 'If month_total=0: 10 × overall_ratio. Else: deficit_ratio × 10 × overall_ratio × absence_ratio',
    insight: 'If +8 has appeared in every month except April across 22 years, its April signal is extremely strong.'
  },
  {
    id: 'W7', name: 'Mirror Diff Reversion', pct: '21% of 70%', color: 'from-purple-600 to-purple-800',
    principle: 'Strongest W1-W9 factor. Mean reversion between opposite-sign diffs (+D vs -D). If the mirror diff (-D) has appeared significantly more than +D this year/month/overall, +D is overdue by reversion pressure. Includes % gap amplification when month-specific gap exceeds overall gap.',
    formula: 'reversion_score = year_imbalance×3 + month_imbalance×1.5 + overall_ratio×5 + amplification×3',
    insight: 'If -8 appeared 4× this year but +8 only 1×, +8 gets strong reversion boost. If April amplifies this gap further, the boost compounds.'
  },
  {
    id: 'W8', name: 'Month Affinity', pct: '13% of 70%', color: 'from-emerald-500 to-emerald-700',
    principle: 'If a diff\'s month-specific frequency exceeds its overall frequency, it historically "favors" this month. This is a positive signal that the diff has natural affinity for the current calendar month.',
    formula: 'If month% > overall%: ((month% - overall%) / overall%) × 10 × overall_ratio',
    insight: '+4 with overall 3.9% but April 5.1% has a 30.8% affinity bonus for April.'
  },
  {
    id: 'W9', name: 'Overdue Boost', pct: '14% of 70%', color: 'from-amber-500 to-amber-700',
    principle: 'Per-ball-specific overdue signal. Computes the average gap between appearances of this specific diff for this ball position. If the current gap exceeds the average, the diff is overdue.',
    formula: 'If gap_ratio > 1.0: min(10, (gap_ratio - 1.0) × 5) × overall_ratio',
    insight: 'If +6 for Ball 2 averages every 12 draws but hasn\'t appeared in 18, gap_ratio=1.5 → boost of 2.5.'
  },
  {
    id: 'W10', name: 'Target Number Recency', pct: '10%', color: 'from-blue-500 to-blue-700',
    principle: 'Evaluates how recently the TARGET NUMBER (last_value + diff) appeared in ANY ball position. Very recent numbers (gap ≤2) get strong boost; cold numbers (gap >15) get heavy penalty. Also includes L15 momentum — how many times the target appeared in the last 15 draws.',
    formula: 'gap≤2: +5, gap≤7: +4, gap≤10: 0, gap≤15: -4, gap>15: -8. L15≥3: +2, L15≥2: +1',
    insight: 'Backtested: +68.2% improvement in Top-1 accuracy, +10.4% in Top-3 over 50 draws. This weight alone was the biggest single accuracy improvement.'
  },
  {
    id: 'W11', name: 'Direction Alternation', pct: '8%', color: 'from-purple-500 to-purple-700',
    principle: 'After two consecutive same-direction moves (++ or --), there\'s a strong statistical tendency to reverse. Historical data: After ++: 76% chance next is DOWN. After --: 72% chance next is UP. Also handles mixed patterns (+- and -+).',
    formula: '++ then -: +8, ++ then +: -5, -- then +: +8, -- then -: -5, +- then +: +4, -+ then -: +4',
    insight: 'The "rubber band" effect — extended moves in one direction create statistical pressure to snap back.'
  },
  {
    id: 'W12', name: 'Position Range', pct: '10%', color: 'from-amber-500 to-amber-700',
    principle: 'Each ball position has a historical p10-p90 range (Ball 1: 2-18, Ball 5: 33-50). Predictions landing outside this range are penalized because they\'re historically unlikely. Inside range gets graduated boost based on proximity to center.',
    formula: 'Outside p10-p90: -6. Inside: max(0, 3 × (1 - distance_from_center))',
    insight: 'Ball 1 predicting value 35 is extremely unlikely (p90=18). This weight prevents such unrealistic predictions from ranking high.'
  },
  {
    id: 'W13', name: 'Markov Transition', pct: '2%', color: 'from-cyan-500 to-cyan-700',
    principle: 'Integrates "What Comes After" Markov chain analysis. For each ball position, computes how often each diff historically followed the current last diff. Diffs that commonly follow get boost; diffs that NEVER followed get penalty.',
    formula: 'Top Markov (≥80% of max): +8, Strong (≥50%): +5, Moderate (≥25%): +2, Rare: +1, Never: -4',
    insight: 'Backtest (100 draws): Neutral impact at all weight levels (±0.4%). Kept at 2% for analytical context enrichment without distorting the proven W1-W12 accuracy.'
  },
  {
    id: 'W14', name: 'Date-Number Signal', pct: '2%', color: 'from-pink-500 to-rose-700',
    principle: 'Cross-correlates draw date components (day, month, year last 2 digits) with ball numbers. Historical analysis shows: Day match 11%, Month match 9.7%, Year match 10.8% — all above 10% random chance. Applied only to Ball 1-3 where signal is meaningful (B4: 2.17%, B5: 0.51% — negligible). Includes month-absent overdue boost: when a month\'s own number has been missing for 2+ years in that month, adds +2 to +5 extra signal (capped at 5).',
    formula: 'B1: Month=+10, Day=+7, Year=+5 | B2: Year=+7, Day=+6, Month=+5 | B3: Year=+5, Day=+3 | B4/B5: Not applied (0%) | Month-absent boost: +years_absent (2-5) when month number overdue',
    insight: 'Ball 1 has strongest month match (6.56%) and day match (3.62%). Ball 2 has strongest year match (4.03%). Ball 4-5 combined rate <2.2% — too low to be meaningful. Month-absent boost validated: e.g., #4 absent from April since 2023 gets +3 extra. See I4 (Digit Group Date Convergence) for multi-signal validation.'
  },
];

const COMBO_CRITERIA = [
  {
    id: 'C1', name: 'Sum Range', weight: '10 pts', category: 'structural',
    principle: 'The sum of 5 selected balls must fall within the historical 10th-90th percentile range. Computed dynamically as mean ± 1.5σ from all historical draws. Sums outside this range have occurred in fewer than 5% of draws on either tail, making them statistically improbable choices for a predicted ticket.',
    formula: 'sum_min = round(avg_sum - 1.5 × std_sum), sum_max = round(avg_sum + 1.5 × std_sum). Pass: +10, Fail: max(0, 5 - distance × 0.5)',
    insight: 'Typical lottery sum range: ~75-175. A draw summing to 30 (e.g., 1+2+3+4+20) has never occurred. This eliminates ~10% of mathematically possible but historically impossible combinations.',
    windows: ['C1/C2/C3 Combo Generation', 'W4: Combo Score Table', 'W28: Combination Validator', 'Class Mix Projections', 'Prediction Maker'],
  },
  {
    id: 'C2', name: 'Spread (B5-B1)', weight: '10 pts', category: 'structural',
    principle: 'The spread between the highest (Ball 5) and lowest (Ball 1) number is computed as B5-B1. This must fall within the historical mean ± 1.5σ range. A narrow spread (e.g., 8) means all 5 numbers cluster in a tiny range — historically almost never happens. A very wide spread (e.g., 49) forces extreme boundary numbers.',
    formula: 'spread_min = round(avg - 1.5σ), spread_max = round(avg + 1.5σ). Pass: +10, Fail: max(0, 5 - dist × 0.5)',
    insight: 'The typical draw spread is 25-49. W20 (Spread Analysis) provides deeper bucketed analysis of 5-unit spread ranges with gap tracking, which feeds into this criterion dynamically.',
    windows: ['C1/C2/C3 Combo Generation', 'W4: Combo Score Table', 'W20: Spread Analysis (bucketed)', 'W28: Combination Validator', 'Prediction Maker'],
  },
  {
    id: 'C2b', name: 'B5-B1 Sweet Zone', weight: '12 pts', category: 'structural',
    principle: 'A refinement of C2 with tighter scoring. The historical "sweet spot" for B5-B1 is 30-44 (highest concentration, ~60% of draws). The acceptable zone is 25-49 (85.6%). Outside these zones, the combination is penalized progressively. This is the third-highest weighted structural criterion because spread correlates strongly with overall ticket viability.',
    formula: '30-44: +12 (sweet zone), 25-29 or 45-49: +8 (acceptable), 20-24 or >49: +3 (warning), <20: 0 (fail)',
    insight: 'A spread of exactly 34-38 is the most common in draw history. This weight ensures generated tickets mimic the "shape" of real winning tickets.',
    windows: ['C1/C2/C3 Combo Generation', 'W28: Combination Validator'],
  },
  {
    id: 'C3', name: 'Odd/Even Balance', weight: '8 pts', category: 'balance',
    principle: 'Counts how many of the 5 balls are odd vs even. The historical distribution is: 3O/2E = 34.6%, 2O/3E = 33.8%, 4O/1E = 14.3%, 1O/4E = 14.3%, 5O/0E = 1.5%, 0O/5E = 1.5%. The score is weighted by each ratio\'s historical probability — 3O/2E gets full weight, while extreme splits get ~4% of the weight.',
    formula: 'Weighted by historical probability: 3O/2E=1.0×, 2O/3E=0.90×, 4O/1E=0.43×, 1O/4E=0.43×',
    insight: 'The parity balance is one of the most stable statistical features across lottery systems worldwide. W11 (Odd/Even & High/Low) provides dedicated cross-analysis with recent draw tracking.',
    windows: ['C1/C2/C3 Combo Generation', 'W4: Combo Score Table', 'W11: Odd/Even & High/Low Analysis', 'W28: Combination Validator', 'Class Mix Projections', 'Prediction Maker'],
  },
  {
    id: 'C4', name: 'High/Low Balance', weight: '12 pts', category: 'balance',
    principle: 'Numbers split into Low (1-25) and High (26-50). The optimal split is 2H/3L (33.6%) or 3H/2L (31.5%), covering ~65% of all draws. Extreme splits (0H/5L or 5H/0L) occur in only ~5% combined. The weight is proportional to historical probability: 3H/2L and 2H/3L get full 12 pts, while 1H or 4H get partial (~4 pts), and 0H or 5H get almost nothing.',
    formula: '2H/3L or 3H/2L: +12, 1H or 4H: +4, 0H or 5H: +0.6',
    insight: 'Combined with C3 (Odd/Even), this forms the "Class Mix" — the fundamental structural fingerprint of a draw. W11 and Class Mix windows provide deep historical tracking of which class combinations are trending.',
    windows: ['C1/C2/C3 Combo Generation', 'W4: Combo Score Table', 'W11: Odd/Even & High/Low Analysis', 'W28: Combination Validator', 'Class Mix Projections', 'Prediction Maker'],
  },
  {
    id: 'C5', name: 'Range Distribution', weight: '8 pts', category: 'balance',
    principle: 'Divides 1-50 into 5 quintiles (1-10, 11-20, 21-30, 31-40, 41-50) and counts how many of the 5 balls fall into each. If any single quintile has more than 3 balls, the combination is structurally imbalanced. This ensures spatial diversity across the number field. Historically, ~92% of draws have no quintile with more than 3 balls.',
    formula: 'max(quintile_counts) ≤ 3: +8 (pass), >3: 0 (fail)',
    insight: 'This prevents degenerate combinations like {11, 13, 15, 17, 19} where all 5 balls are from a single quintile. Such combinations, while mathematically valid, almost never win.',
    windows: ['C1/C2/C3 Combo Generation', 'W28: Combination Validator'],
  },
  {
    id: 'C6', name: 'Consecutive Numbers', weight: '7 pts', category: 'pattern',
    principle: 'Counts consecutive pairs (e.g., 14-15, 31-32) within the sorted 5-ball combination. Historically, ~70% of draws have 0 consecutive pairs, ~25% have exactly 1 pair, and <5% have 2 or more. Having multiple consecutive numbers dramatically reduces a ticket\'s statistical viability because it forces clustering in the number space.',
    formula: '0-1 consecutive pairs: +7 (pass), 2+: +3 (warning)',
    insight: 'A single consecutive pair like (22, 23) is acceptable and natural. Two pairs like (5, 6, 22, 23, 40) is extremely rare in practice.',
    windows: ['C1/C2/C3 Combo Generation', 'W28: Combination Validator'],
  },
  {
    id: 'C7', name: 'Prime Count', weight: '6 pts', category: 'pattern',
    principle: 'Counts primes among the 5 balls (primes in 1-50: 2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47 = 15 total). With 15/50 = 30% being prime, the expected count per draw is ~1.5. Having 1-4 primes (covers ~95% of draws) passes; 0 or 5 primes is flagged as unusual.',
    formula: '1-4 primes: +6 (pass), 0 or 5: +2 (warning)',
    insight: 'Primes are distributed non-uniformly across 1-50 (denser below 20). This criterion indirectly enforces range spread.',
    windows: ['C1/C2/C3 Combo Generation', 'W28: Combination Validator'],
  },
  {
    id: 'C8', name: 'Boundary Numbers', weight: '6 pts', category: 'pattern',
    principle: 'Counts balls at the extremes of the 1-50 range: ≤5 (very low) or ≥46 (very high). These 10 "boundary" numbers represent 20% of the field. Having more than 2 boundary numbers in a 5-ball draw is statistically unlikely (~3% of draws) because it forces an unnatural spread pattern.',
    formula: '0-2 boundary numbers: +6 (pass), 3+: +2 (warning)',
    insight: 'A draw like {1, 3, 46, 48, 50} has 5 boundary numbers — this pattern has virtually never occurred in draw history.',
    windows: ['C1/C2/C3 Combo Generation', 'W28: Combination Validator'],
  },
  {
    id: 'C9', name: 'Lucky Star Sum', weight: '8 pts', category: 'structural',
    principle: 'The sum of both Lucky Stars (LS1 + LS2) must fall within the historical 2σ range (wider tolerance than ball sum because of the smaller number space). With LS values from 1-12, the sum ranges from 3 to 23. The percentile range is computed dynamically from all historical LS sums. Outside this range, the LS pair is considered structurally unusual.',
    formula: 'ls_sum_min = max(3, round(avg - 2σ)), ls_sum_max = min(23, round(avg + 2σ)). Pass: +8, Fail: max(0, 4 - dist)',
    insight: 'The LS sum range is more tolerant (2σ vs 1.5σ for balls) because the smaller LS number space creates more natural variance.',
    windows: ['C1/C2/C3 Combo Generation', 'W28: Combination Validator', 'Prediction Maker'],
  },
  {
    id: 'C10', name: 'Round Numbers', weight: '6 pts', category: 'pattern',
    principle: 'Counts multiples of 5 (5, 10, 15, 20, 25, 30, 35, 40, 45, 50) among the 5 balls. With 10/50 = 20% being round, the expected count is ~1.0. Having 3+ round numbers is flagged. This criterion exists because players often over-select round numbers (cognitive bias), so it acts as a normalizer.',
    formula: '0-2 round numbers: +6 (pass), 3+: +2 (warning)',
    insight: 'Multiples of 5 are psychologically "attractive" numbers but statistically no more likely. This prevents bias-driven clustering.',
    windows: ['C1/C2/C3 Combo Generation', 'W28: Combination Validator'],
  },
  {
    id: 'C11', name: 'Gap Mix', weight: '12 pts', category: 'recency',
    principle: 'The core recency criterion. For each of the 5 balls, computes the "gap" (draws since last appearance). Classifies each into Recent (gap ≤5), Midzone (6-15), or Overdue (16+). The ideal ticket contains a balanced mix matching the historical distribution: 46.5% Recent, 35.1% Midzone, 18.4% Overdue. Having ≥2 recent + ≥1 midzone is optimal; all-overdue tickets perform poorly.',
    formula: '≥2R + ≥1M: +12, ≥2R + ≥1O: +10, ≥1R + ≥2M: +9, ≥1R + any: +7, avg_gap<30: +4, else: +2',
    insight: 'This is the most powerful combination-level predictor. The gap distribution directly maps to the number\'s "temperature" — how active it is in the current draw cycle. W5 and W14 (Hot/Cold analysis) provide per-number gap tracking that feeds into this.',
    windows: ['C1/C2/C3 Combo Generation', 'W5: Hot/Cold Temperature Map', 'W14: Number Frequency & Gap Analysis', 'W28: Combination Validator', 'Prediction Maker'],
  },
  {
    id: 'C12', name: 'Recent Momentum', weight: '15 pts', category: 'recency',
    principle: 'Highest-weighted combination criterion. Sums the total appearances of all 5 selected balls across the last 20 draws. A total of ≥12 indicates strong momentum (multiple balls are currently active). This captures the "hot hand" effect: numbers that have appeared recently are statistically more likely to appear again in the near term due to the non-uniform distribution of draw sequences.',
    formula: 'Total last-20 appearances: ≥12: +15, ≥8: +12, ≥5: +8, <5: +3',
    insight: 'With 5 balls drawn from 50, each number appears ~every 10 draws on average. A ticket where each ball appeared 2-3× in last 20 draws (total ~12) is riding strong recent momentum.',
    windows: ['C1/C2/C3 Combo Generation', 'W5: Hot/Cold Temperature', 'W14: Number Frequency', 'W28: Combination Validator', 'Summary Dashboard'],
  },
  {
    id: 'C13', name: 'LS Recent Momentum', weight: '8 pts', category: 'recency',
    principle: 'Same concept as C12 but for Lucky Stars. Sums LS1 and LS2 appearances in the last 20 draws. Because the LS pool is only 12 numbers (vs 50 for balls), each LS value appears ~every 6 draws, making momentum a stronger predictor. A combined LS momentum ≥6 indicates both stars are currently active.',
    formula: 'LS total last-20: ≥6: +8, ≥3: +5, <3: +2',
    insight: 'Lucky Stars have a smaller pool (12) so their momentum cycles are shorter and more predictable than main balls.',
    windows: ['C1/C2/C3 Combo Generation', 'W34: Lucky Star Difference Analysis', 'W28: Combination Validator'],
  },
  {
    id: 'C14', name: 'LS Pair History', weight: '6 pts', category: 'pattern',
    principle: 'Checks whether the specific Lucky Star combination (e.g., LS 3+7) has appeared together in any historical draw. With C(12,2)=66 possible LS pairs and ~2000 draws, each pair should appear ~30 times on average. However, the distribution is non-uniform — some pairs appear 50+ times while others appear <10 times. Known pairs get full bonus; never-seen pairs get partial.',
    formula: 'Historical count ≥1: +6 (pass), 0: +3 (partial)',
    insight: 'Some LS pairs are "magnetically" attracted — (2,3) and (4,7) appear far more often than chance predicts. W10 tracks LS pair gap analysis.',
    windows: ['C1/C2/C3 Combo Generation', 'W35: Lucky Star Pair Analysis', 'W28: Combination Validator'],
  },
  {
    id: 'C15', name: 'Multiples of 3', weight: '5 pts', category: 'pattern',
    principle: 'Counts balls divisible by 3 (3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36, 39, 42, 45, 48 = 16 total). With 16/50 = 32%, the expected count per draw is ~1.6. Having ≥4 multiples of 3 creates an unnatural clustering pattern. This criterion is relatively low-weighted (5 pts) because it acts as a soft structural filter.',
    formula: '0-3 multiples of 3: +5 (pass), 4+: +2 (warning)',
    insight: 'Combined with C7 (Primes) and C10 (Round Numbers), this forms a trio of "number class" filters that prevent structural anomalies.',
    windows: ['C1/C2/C3 Combo Generation', 'W28: Combination Validator'],
  },
  {
    id: 'C16', name: 'Unique Combination', weight: '4 pts', category: 'validation',
    principle: 'Verifies the exact 5-ball combination is not an exact repeat of any historical draw. With C(50,5) = 2,118,760 possible combinations and ~2000 historical draws, the probability of a random repeat is ~0.09%. This is the lowest-weighted criterion because repeats are already extremely unlikely — it exists primarily as a psychological confidence filter.',
    formula: 'Not in historical set: +4 (pass), Repeat: 0',
    insight: 'In ~2000 historical draws, no exact 5-ball combination has ever repeated. This criterion is virtually always passed.',
    windows: ['C1/C2/C3 Combo Generation', 'W28: Combination Validator'],
  },
  {
    id: 'C17', name: 'Digit Rebound', weight: '8 pts', category: 'recency',
    principle: 'Identifies which unit digits (0-9) are completely MISSING from the last 5 draws across all ball positions. If digit 7 hasn\'t appeared in 5 draws (meaning no 7, 17, 27, 37, or 47 were drawn), numbers ending in 7 are "digit-overdue." Balls whose unit digit fills a missing digit gap get proportional bonus. This captures a pattern where missing digits tend to "rebound" within a few draws.',
    formula: '≥2 digit rebounds: +min(count × 2.5 + LS_count × 1.5, 8). 1 rebound: +2.5. 0: 0',
    insight: 'With 10 possible digits and 5 balls per draw, on average 5 digits appear per draw. After 5 draws, almost all digits have appeared — making truly missing digits rare and significant when they occur.',
    windows: ['C1/C2/C3 Combo Generation', 'Digit Affinity Analysis', 'W28: Combination Validator'],
  },
  {
    id: 'C18', name: 'Diff Repeat Guard', weight: '+4 bonus only', category: 'directional',
    principle: 'Examines position-by-position differences (new_ball[i] - last_ball[i]) and checks if they would repeat the exact same diffs as the previous draw. Historically, 89.9% of draws have ZERO positions with the same diff repeated. Also tracks direction consistency: if ≥4 positions move in the same direction as last draw, it\'s unusual (only 6.1% of draws). Originally included penalties, but backtest proved penalties killed combination diversity — now bonus-only.',
    formula: '0 same diffs: +4, ≤1 same direction: +3. All penalties removed (backtest-validated: +13% coverage improvement)',
    insight: 'Backtest ablation showed that C18 penalties eliminated many viable 2-ball-match combinations. Converting to bonus-only preserved the "reward good patterns" signal while removing the "punish diversity" effect.',
    windows: ['C1/C2/C3 Combo Generation', 'W3: Per-Ball Difference (uses W11 Dir Alternation equivalent)', 'W28: Combination Validator'],
  },
  {
    id: 'C19', name: 'Adjacent Diff Correlation', weight: '+8 bonus only', category: 'directional',
    principle: 'Measures how adjacent ball positions (B1-B2, B2-B3, B3-B4, B4-B5) correlate in their movement direction. Historically, adjacent positions move in the SAME direction 69.4% of the time (both up or both down). Also checks if adjacent diffs are within ±2 of each other (25.7% of draws, which is 1.7× more than non-adjacent pairs). This captures inter-position momentum: when Ball 2 rises, Ball 3 tends to rise too.',
    formula: '≥3 adj same dir: +8, ≥2: +5, ≥1: +2. Close diffs (±2): +3 bonus. No penalties (backtest-validated)',
    insight: 'The 69.4% same-direction rate between adjacent balls is one of the strongest structural patterns in this lottery. It suggests an underlying momentum field that connects ball positions.',
    windows: ['C1/C2/C3 Combo Generation', 'W3: Per-Ball Difference', 'W28: Combination Validator'],
  },
  {
    id: 'C20', name: 'Direction Alternation', weight: '+10 to -8', category: 'directional',
    principle: 'Applied at the combination level across all 5 ball positions. Examines the last 2 directional moves per position: if a position has moved ++ (up twice), historical data shows 76% chance the next move is DOWN. Similarly, -- has 72% chance of reversing to UP. Tickets that follow these reversal patterns are rewarded; tickets that continue unfavorable patterns (e.g., +++ continuation) are penalized.',
    formula: '≥3 favorable reversals: +10, ≥2: +6. ≥3 unfavorable continuations: -8, ≥2: -4',
    insight: 'This is the same principle as W11 (Per-Ball Direction Alternation) applied at the combination level. The "rubber band" effect is one of the most consistent patterns — 76% reversal after ++ is remarkably stable across all ball positions and time periods.',
    windows: ['C1/C2/C3 Combo Generation', 'W3: Per-Ball Difference (W11 weight)', 'W28: Combination Validator'],
  },
  {
    id: 'C21', name: 'Position Range Enforcement', weight: '+6 to -6', category: 'structural',
    principle: 'Each ball position has a historical p10-p90 range based on sorted-order analysis: Ball 1 (2-18), Ball 2 (7-28), Ball 3 (13-37), Ball 4 (22-44), Ball 5 (33-50). These ranges represent where 80% of historical values fall. All 5 balls falling within their respective position ranges indicates a structurally normal ticket. Having 2+ balls outside their ranges indicates an unusual structural shape.',
    formula: 'All 5 in range: +6. 1 out: +3 (warning). 2+ out: -6 (penalty)',
    insight: 'This is the same principle as W12 (Per-Ball Position Range) applied at the combination level. It prevents structurally impossible tickets like Ball 1=45 (historical range 2-18).',
    windows: ['C1/C2/C3 Combo Generation', 'W3: Per-Ball Difference (W12 weight)', 'W28: Combination Validator'],
  },
  {
    id: 'C22', name: 'HML Position Profile', weight: '+8 to +23', category: 'structural',
    principle: 'Coverage-based HML classification (50/30/20): For each ball position, numbers sorted by appearance frequency — top 50% coverage = H, next 30% = M, remaining 20% = L. Each number is also tagged by how many positions it is H at: H2 (Dual — H at 2 positions), H1 (Positional — H at 1 position only), M-only, L-only. For B1-B5 only (LS1/LS2 are independent). Applied to ALL combo generators.',
    formula: 'Per ball: H position = +3, M position = +1, L position = -2 (max +15). HHHHH Composition Bonus: 3-4 Dual H + 1-2 Positional H = +8 (proven pattern from 2026 data: 66% Dual + 34% Positional in historical HHHHH draws)',
    insight: 'The 2026 HHHHH analysis proved that Dual H numbers (H at 2 positions — versatile) dominate at 66%. Only 6 numbers are Dual H: 7, 19, 20, 30, 35, 44. Optimal HHHHH ticket = 3-4 from this pool + 1-2 Positional H specialists. This principle rewards structurally sound combos where each number is naturally strong at its assigned position.',
    windows: ['C1/C2/C3 Combo Generation', 'W7: HML Classification', 'W28: Combination Validator', 'My Picks', 'Pattern Predictor'],
  },
];

const DIVERSITY_PRINCIPLES = [
  {
    name: 'Candidate Pool Expansion (15 → 25)',
    detail: 'Expanded from 15 to 25 candidates per ball position. Position-aware selection ensures Ball 1 candidates favor the 2-18 range (p10-p90) while Ball 5 candidates favor 33-50. Within each position, candidates are balanced across gap zones: ~47% Recent (appeared in last 5 draws), ~35% Midzone (6-15 draws ago), ~18% Overdue (16+ draws ago).',
    impact: 'Unique numbers across 10 tickets improved from 14 to 18-19 (+29%).',
  },
  {
    name: 'Diversity-Enforced Ticket Selection',
    detail: 'After scoring all combinations, tickets are selected greedily with diversity constraints: each of the first 5 tickets must introduce ≥2 numbers not in any previously selected ticket, and the last 5 must introduce ≥1. Maximum 3 numbers can overlap with any single existing ticket. This prevents the common failure mode where all 10 tickets converge on the same 14 "optimal" numbers.',
    impact: '≥2 ball coverage improved from 52% to 65% in backtesting.',
  },
  {
    name: 'Balanced Gap Distribution',
    detail: 'Candidates are selected proportionally: ~47% Recent (gap ≤5), ~35% Midzone (6-15), ~18% Overdue (16+), matching the historical distribution of 46.5% / 35.1% / 18.4%. This ensures tickets contain both "hot" momentum numbers and "due" overdue numbers rather than over-concentrating on either extreme.',
    impact: 'Ensures tickets contain both "hot" and "due" numbers rather than over-concentrating.',
  },
];

const CategoryBadge = ({ category }) => {
  const colors = {
    structural: 'bg-blue-50 text-blue-700 border-blue-200',
    balance: 'bg-green-50 text-green-700 border-green-200',
    pattern: 'bg-purple-50 text-purple-700 border-purple-200',
    recency: 'bg-amber-50 text-amber-700 border-amber-200',
    directional: 'bg-rose-50 text-rose-700 border-rose-200',
    validation: 'bg-gray-50 text-gray-600 border-gray-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${colors[category] || 'bg-gray-50'}`}>
      {category}
    </span>
  );
};

const WindowTag = ({ name }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 font-medium">
    {name}
  </span>
);

export default function WeightsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('combo');
  const [expandedWeight, setExpandedWeight] = useState(null);

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="weights-page">
      {/* Header */}
      <div className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-600" data-testid="back-button">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-lg font-bold tracking-tight text-gray-900">W42: Weights & Principles</h1>
              <p className="text-xs text-gray-400">The statistical engine behind every prediction</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Architecture Overview */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-5" data-testid="architecture-overview">
          <h2 className="text-base font-bold mb-3 flex items-center gap-2 text-indigo-900">
            <Info size={18} className="text-indigo-600" />
            Two-Layer Scoring Architecture
          </h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="bg-white rounded-lg p-4 border border-purple-100 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Scales size={20} className="text-purple-600" />
                <span className="font-bold text-purple-800">Layer 1: Combination Evaluation (21 Criteria)</span>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">
                Evaluates complete 5-ball + 2-LS tickets against 21 statistical boundaries.
                Answers: <span className="text-gray-800 font-medium">"Is this full combination statistically viable?"</span>
              </p>
              <p className="text-xs text-purple-600 mt-2">Used in: C1/C2/C3 Combo Generation, W28 Validator</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-indigo-100 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <NumberCircleOne size={20} className="text-cyan-600" />
                <span className="font-bold text-cyan-800">Layer 2: Per-Ball Prediction (13 Weights)</span>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">
                Scores every possible diff for each ball position independently.
                Answers: <span className="text-gray-800 font-medium">"What single number should come next for Ball N?"</span>
              </p>
              <p className="text-xs text-cyan-600 mt-2">Used in: Window 3 "Most Deserving Difference"</p>
            </div>
          </div>
        </div>

        {/* Tab Selector */}
        <div className="flex gap-2 border-b border-gray-200 pb-0" data-testid="tab-selector">
          {[
            { key: 'combo', label: 'Combo Criteria (C1-C21)', icon: <Scales size={16} /> },
            { key: 'perball', label: 'Per-Ball Weights (W1-W14)', icon: <Target size={16} /> },
            { key: 'indicators', label: 'Cross-Analysis Indicators', icon: <Lightning size={16} /> },
            { key: 'crossover', label: 'Per-Ball vs Combo', icon: <ArrowsClockwise size={16} /> },
            { key: 'diversity', label: 'Diversity Engine', icon: <ArrowsClockwise size={16} /> },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setExpandedWeight(null); }}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-[1px] ${
                activeTab === tab.key
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Combo Criteria */}
        {activeTab === 'combo' && (
          <div className="space-y-3" data-testid="combo-section">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">Combination Evaluation — 21 Criteria</h2>
              <span className="text-xs text-gray-400">Applied to each 5-ball + 2-LS ticket</span>
            </div>

            {/* Category legend */}
            <div className="flex flex-wrap gap-2">
              {['structural', 'balance', 'pattern', 'recency', 'directional', 'validation'].map(c => (
                <CategoryBadge key={c} category={c} />
              ))}
            </div>

            {COMBO_CRITERIA.map((c) => (
              <div
                key={c.id}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-sm transition cursor-pointer"
                onClick={() => setExpandedWeight(expandedWeight === c.id ? null : c.id)}
                data-testid={`criterion-${c.id}`}
              >
                <div className="flex items-center gap-3 p-4">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-xs font-black text-gray-600 shrink-0 border border-gray-200">
                    {c.id}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-gray-900">{c.name}</span>
                      <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-mono border border-emerald-200">{c.weight}</span>
                      <CategoryBadge category={c.category} />
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{c.principle}</p>
                  </div>
                  <div className="text-gray-300 text-xs">{expandedWeight === c.id ? '▲' : '▼'}</div>
                </div>
                {expandedWeight === c.id && (
                  <div className="px-4 pb-4 pt-0 space-y-2 border-t border-gray-100">
                    <div className="bg-gray-50 rounded-lg p-3 mt-2">
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Principle</p>
                      <p className="text-xs text-gray-700 leading-relaxed">{c.principle}</p>
                    </div>
                    {c.formula && (
                      <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                        <p className="text-[10px] uppercase tracking-wider text-indigo-400 mb-1">Formula</p>
                        <code className="text-xs text-indigo-700 font-mono">{c.formula}</code>
                      </div>
                    )}
                    <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                      <p className="text-[10px] uppercase tracking-wider text-amber-500 mb-1">Insight</p>
                      <p className="text-xs text-amber-800">{c.insight}</p>
                    </div>
                    {/* Applied Windows */}
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400 mb-2">Applied In</p>
                      <div className="flex flex-wrap gap-1.5">
                        {c.windows.map((w, i) => (
                          <WindowTag key={i} name={w} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Per-Ball Weights */}
        {activeTab === 'perball' && (
          <div className="space-y-3" data-testid="perball-section">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">Per-Ball Scoring System — 13 Weights</h2>
              <span className="text-xs text-gray-400">W1-W9 share 70% | W10-W13 share 30%</span>
            </div>

            {/* Weight allocation bar */}
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex h-7 rounded-md overflow-hidden text-[9px] font-bold text-white">
                <div className="bg-gradient-to-r from-gray-500 to-gray-600 flex items-center justify-center" style={{width: '70%'}}>W1-W9: 70%</div>
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center" style={{width: '10%'}}>W10</div>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-center" style={{width: '8%'}}>W11</div>
                <div className="bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center" style={{width: '10%'}}>W12</div>
                <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 flex items-center justify-center" style={{width: '2%'}} title="W13: 2%"></div>
              </div>
            </div>

            {PERBALL_WEIGHTS.map((w) => (
              <div
                key={w.id}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-sm transition cursor-pointer"
                onClick={() => setExpandedWeight(expandedWeight === w.id ? null : w.id)}
                data-testid={`weight-${w.id}`}
              >
                <div className="flex items-center gap-3 p-4">
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${w.color} flex items-center justify-center text-sm font-black text-white shrink-0`}>
                    {w.id}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-gray-900">{w.name}</span>
                      <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{w.pct}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{w.principle}</p>
                  </div>
                  <div className="text-gray-300 text-xs">{expandedWeight === w.id ? '▲' : '▼'}</div>
                </div>
                {expandedWeight === w.id && (
                  <div className="px-4 pb-4 pt-0 space-y-2 border-t border-gray-100">
                    <div className="bg-gray-50 rounded-lg p-3 mt-2">
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Principle</p>
                      <p className="text-xs text-gray-700 leading-relaxed">{w.principle}</p>
                    </div>
                    {w.formula && (
                      <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                        <p className="text-[10px] uppercase tracking-wider text-indigo-400 mb-1">Formula</p>
                        <code className="text-xs text-indigo-700 font-mono">{w.formula}</code>
                      </div>
                    )}
                    <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                      <p className="text-[10px] uppercase tracking-wider text-amber-500 mb-1">Insight</p>
                      <p className="text-xs text-amber-800">{w.insight}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}



        {/* Cross-Analysis Indicators */}
        {activeTab === 'indicators' && (
          <div className="space-y-6" data-testid="indicators-section">
            <div>
              <h2 className="text-base font-bold text-gray-900">Cross-Analysis Indicators</h2>
              <p className="text-xs text-gray-400 mt-1">Signals derived from cross-referencing different data sources (balls vs lucky stars, digit groups, position tiers).</p>
            </div>

            {/* Indicator 1: LS Digit Signal */}
            <div className="bg-white border border-orange-200 rounded-xl overflow-hidden" data-testid="indicator-ls-digit">
              <div className="p-5 border-b border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center text-white font-bold">I1</div>
                  <div>
                    <h3 className="font-bold text-gray-900">LS Digit Signal</h3>
                    <p className="text-[10px] text-gray-500">Cross-correlation between Lucky Star digits and missing ball digit groups</p>
                  </div>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <h4 className="text-sm font-bold text-gray-800">Principle</h4>
                  <p className="text-xs text-gray-600 mt-1">When a last-digit group (e.g., numbers ending in 2: 2, 12, 22, 32, 42) has NO appearances in the last 5 draws' ball numbers, but the same digit DOES appear in Lucky Stars during those draws, this creates a positive signal for the digit group appearing in the next draw.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-gray-800 mb-2">Historical Evidence (1936 draws)</h4>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-3 py-2 text-left font-bold text-gray-500">Scenario</th>
                        <th className="px-3 py-2 text-right font-bold text-gray-500">Hit Rate</th>
                        <th className="px-3 py-2 text-right font-bold text-gray-500">Sample</th>
                        <th className="px-3 py-2 text-right font-bold text-gray-500">vs Baseline</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="px-3 py-2 text-gray-700">Digit present in balls (baseline)</td>
                        <td className="px-3 py-2 text-right font-mono">42.0%</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">18,086</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">-</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="px-3 py-2 text-gray-700">Digit missing from balls AND LS</td>
                        <td className="px-3 py-2 text-right font-mono">42.8%</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">404</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">+0.8%</td>
                      </tr>
                      <tr className="border-b border-gray-100 bg-amber-50">
                        <td className="px-3 py-2 text-amber-800 font-bold">Digit missing from balls, 1x in LS</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-amber-800">47.9%</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">457</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-green-600">+5.9%</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="px-3 py-2 text-gray-700">Digit missing from balls, 2x in LS</td>
                        <td className="px-3 py-2 text-right font-mono">39.7%</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">262</td>
                        <td className="px-3 py-2 text-right font-mono text-red-500">-2.3%</td>
                      </tr>
                      <tr className="bg-orange-50">
                        <td className="px-3 py-2 text-orange-800 font-bold">Digit missing from balls, 3x+ in LS</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-orange-800">50.0%</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">76</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-green-600">+8.0%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-800">Same Number vs Different Number</h4>
                  <p className="text-xs text-gray-600 mt-1">When the digit hits, does the exact same LS number appear as a ball, or a different number in the group?</p>
                  <div className="flex gap-4 mt-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex-1">
                      <p className="text-xs font-bold text-blue-800">Different number</p>
                      <p className="text-2xl font-black text-blue-700">95.1%</p>
                      <p className="text-[10px] text-blue-600">e.g., LS=2 but Ball=12, 22, 32, or 42</p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex-1">
                      <p className="text-xs font-bold text-gray-600">Same number</p>
                      <p className="text-2xl font-black text-gray-500">24.1%</p>
                      <p className="text-[10px] text-gray-400">e.g., LS=2 and Ball=2 (only possible for 1-12)</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2">The signal is about the digit group warming up, not the specific number. Higher numbers (20s-40s) in the group tend to hit more often.</p>
                </div>

                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <h4 className="text-xs font-bold text-indigo-800">Applied In</h4>
                  <p className="text-[10px] text-indigo-600 mt-0.5">Class Mix Projections: Smart Picks scoring. Missing digit group +25 base, +8 if LS 1x (MODERATE), +15 if LS 3x+ (STRONG).</p>
                </div>
              </div>
            </div>

            {/* Indicator 2: HML Tier Analysis */}
            <div className="bg-white border border-indigo-200 rounded-xl overflow-hidden" data-testid="indicator-hml-tier">
              <div className="p-5 border-b border-indigo-200 bg-gradient-to-r from-indigo-50 to-blue-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center text-white font-bold">I2</div>
                  <div>
                    <h3 className="font-bold text-gray-900">HML Tier Deficit</h3>
                    <p className="text-[10px] text-gray-500">Position-aware frequency tier analysis for pattern prediction</p>
                  </div>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-xs text-gray-600">Each ball position classifies numbers as High/Medium/Low based on overall frequency (B1/B5: top 9=H, next 10=M; B2/3/4: top 14=H, rest by rounded %). When a tier is under-represented in the current year vs historical average, it creates reversion pressure.</p>
                <p className="text-xs text-gray-600">Underperforming numbers within each tier are identified using proportional expected count: <code className="bg-gray-100 px-1 rounded text-[10px]">actual_cy &lt; (overall% x cy_draws) x 0.5</code></p>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <h4 className="text-xs font-bold text-indigo-800">Applied In</h4>
                  <p className="text-[10px] text-indigo-600 mt-0.5">Window 7 (Individual Ball Analysis), Pattern Predictor (Indicator 1: Tier Deficit), C1/C2/C3 HML constraint sets for ticket generation.</p>
                </div>
              </div>
            </div>

            {/* Indicator 3: Recency-Weighted Tier Signal */}
            <div className="bg-white border border-green-200 rounded-xl overflow-hidden" data-testid="indicator-recency-tier">
              <div className="p-5 border-b border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center text-white font-bold">I3</div>
                  <div>
                    <h3 className="font-bold text-gray-900">Recency-Weighted Tier Signal</h3>
                    <p className="text-[10px] text-gray-500">Hot/Cold distribution within each HML tier per position</p>
                  </div>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-xs text-gray-600">Within each H/M/L tier for each ball position, numbers are classified as Hot (appeared in last 10 draws), Warm (last 20), or Cold (not in last 20). A tier that is mostly cold numbers signals "DUE" — the tier's numbers are overdue for appearance. A tier that is mostly hot signals "ACTIVE" — continuation likely.</p>
                <p className="text-xs text-gray-600">Cold numbers with high overall frequency have ~50% chance of appearing vs ~20% for cold numbers with low frequency. This combines with the HML tier deficit to identify the strongest candidates.</p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <h4 className="text-xs font-bold text-green-800">Applied In</h4>
                  <p className="text-[10px] text-green-600 mt-0.5">Pattern Predictor (Indicator 3), Number Prediction B1-B5 (W11 Hot/Cold signal in consensus scoring).</p>
                </div>
              </div>
            </div>

            {/* Indicator 4: Digit Group Date Convergence */}
            <div className="bg-white border border-rose-200 rounded-xl overflow-hidden" data-testid="indicator-dg-convergence">
              <div className="p-5 border-b border-rose-200 bg-gradient-to-r from-rose-50 to-pink-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center text-white font-bold">I4</div>
                  <div>
                    <h3 className="font-bold text-gray-900">Digit Group Date Convergence</h3>
                    <p className="text-[10px] text-gray-500">Minor supporting signal — strength comes from multi-indicator convergence, not standalone</p>
                  </div>
                  <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border bg-amber-50 text-amber-700 border-amber-200">Supporting</span>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <h4 className="text-sm font-bold text-gray-800">Principle</h4>
                  <p className="text-xs text-gray-600 mt-1">When multiple independent date-based signals converge on the same digit group, the combined prediction becomes stronger than any individual signal. No single date-based digit group indicator provides meaningful edge over random (~42.3% base rate), but convergence of 3+ signals creates actionable bias.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-gray-800 mb-2">Standalone Signal Validation (1936 draws)</h4>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-3 py-2 text-left font-bold text-gray-500">Individual Signal</th>
                        <th className="px-3 py-2 text-right font-bold text-gray-500">Hit Rate</th>
                        <th className="px-3 py-2 text-right font-bold text-gray-500">Tests</th>
                        <th className="px-3 py-2 text-right font-bold text-gray-500">vs Random</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="px-3 py-2 text-gray-700">Missing DG fills next draw</td>
                        <td className="px-3 py-2 text-right font-mono">40.5%</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">1,182</td>
                        <td className="px-3 py-2 text-right font-mono text-red-500">-1.8%</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="px-3 py-2 text-gray-700">Highest DG reappears next draw</td>
                        <td className="px-3 py-2 text-right font-mono">43.5%</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">1,340</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">+1.2%</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="px-3 py-2 text-gray-700">Dominating DG on calendar date</td>
                        <td className="px-3 py-2 text-right font-mono">45.7%</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">311</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">+3.4%</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="px-3 py-2 text-gray-700">DG Projection (gap+reappear+2024)</td>
                        <td className="px-3 py-2 text-right font-mono">44.2%</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">840</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">+1.9%</td>
                      </tr>
                      <tr className="bg-rose-50">
                        <td className="px-3 py-2 text-rose-800 font-bold">Baseline (any DG, random)</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-rose-800">42.3%</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">-</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-400">-</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="text-[10px] text-gray-500 mt-2">None individually significant. P(at least 1 from 5-number group with 5 balls drawn from 50) = 1 - C(45,5)/C(50,5) = 42.3%</p>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-800">When Convergence Becomes Meaningful</h4>
                  <p className="text-xs text-gray-600 mt-1">The edge comes when 3+ independent signals align on the same digit group:</p>
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-start gap-2 text-xs text-gray-700">
                      <span className="w-5 h-5 rounded-full bg-rose-100 text-rose-700 font-bold text-[10px] flex items-center justify-center shrink-0 mt-0.5">1</span>
                      <span><strong>Same-date dominance</strong> — DG appears disproportionately in draws on this exact calendar date (e.g., Group 4 at 32% on Apr 14)</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs text-gray-700">
                      <span className="w-5 h-5 rounded-full bg-rose-100 text-rose-700 font-bold text-[10px] flex items-center justify-center shrink-0 mt-0.5">2</span>
                      <span><strong>All same-day dominance</strong> — DG is over-represented across ALL draws on this day of month (e.g., Group 4 at 13.7% on all 14th draws vs 10.4% expected)</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs text-gray-700">
                      <span className="w-5 h-5 rounded-full bg-rose-100 text-rose-700 font-bold text-[10px] flex items-center justify-center shrink-0 mt-0.5">3</span>
                      <span><strong>W14 Date-Number overdue</strong> — The month/day number itself belongs to this DG AND has been absent for 2+ years (e.g., #4 absent from April since 2023)</span>
                    </div>
                  </div>
                </div>

                <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                  <h4 className="text-xs font-bold text-rose-800">Example: April 14 Draw</h4>
                  <div className="mt-2 space-y-1 text-xs text-gray-700">
                    <p>Signal 1: Group 4 = 32% on Apr 14 (vs 10.4% overall) <span className="font-bold text-rose-700">+21.6% over-representation</span></p>
                    <p>Signal 2: Group 4 = 13.7% on all 14th draws (vs 10.4% overall) <span className="font-bold text-rose-700">+3.3% edge (63 draws, 315 balls)</span></p>
                    <p>Signal 3: #4 is April's month number, absent since 2023 <span className="font-bold text-rose-700">3 years overdue (W14 boost active)</span></p>
                    <p className="text-[10px] text-gray-500 mt-1">Three independent datasets, three different sample sizes, all pointing to Group 4 (4, 14, 24, 34, 44).</p>
                  </div>
                </div>

                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <h4 className="text-xs font-bold text-indigo-800">Applied In</h4>
                  <p className="text-[10px] text-indigo-600 mt-0.5">W14: Date-Number Signal (month-absent overdue boost), Calendar Date Analysis (Digit Group Comparison tab, Monthly Breakdown). Supporting signal only — not a standalone weight.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Per-Ball vs Combo Crossover */}
        {activeTab === 'crossover' && (
          <div className="space-y-6" data-testid="crossover-section">
            <div>
              <h2 className="text-base font-bold text-gray-900">Per-Ball vs Combo — Same Principle, Two Layers</h2>
              <p className="text-xs text-gray-400 mt-1">Some weights exist at both the per-ball level (scoring individual diffs) and the combo level (evaluating full tickets). Here they are side by side.</p>
            </div>

            {/* W11 vs C20: Direction Alternation */}
            <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid="crossover-w11-c20">
              <div className="bg-purple-50 border-b border-purple-200 px-4 py-3">
                <h3 className="font-bold text-sm text-purple-800">Direction Alternation — W11 vs C20</h3>
                <p className="text-[11px] text-purple-600 mt-0.5">After two consecutive same-direction moves, the next move tends to reverse</p>
              </div>
              <div className="grid md:grid-cols-2 divide-x divide-gray-200">
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-xs font-black text-white">W11</div>
                    <div>
                      <p className="font-bold text-sm text-gray-900">Per-Ball Level</p>
                      <p className="text-[10px] text-purple-600">Weight: 8% of total score</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs text-gray-600">
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <p className="text-[10px] uppercase text-gray-400 mb-1">Scope</p>
                      <p>Evaluates <strong>one ball position at a time</strong>. Looks at the last 2 directional moves for that specific position (e.g., Ball 3 went ++ in last 2 draws).</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-2.5 border border-purple-100">
                      <p className="text-[10px] uppercase text-purple-400 mb-1">Scoring</p>
                      <p><code className="text-purple-700">++ then -: +8</code> &nbsp; <code className="text-purple-700">++ then +: -5</code></p>
                      <p><code className="text-purple-700">-- then +: +8</code> &nbsp; <code className="text-purple-700">-- then -: -5</code></p>
                      <p><code className="text-purple-700">+- then +: +4</code> &nbsp; <code className="text-purple-700">-+ then -: +4</code></p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2.5 border border-amber-100">
                      <p className="text-[10px] uppercase text-amber-500 mb-1">Used in</p>
                      <p>Window 3 "Most Deserving Difference" — ranks which diff should come next for each ball.</p>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-xs font-black text-gray-600 border border-gray-200">C20</div>
                    <div>
                      <p className="font-bold text-sm text-gray-900">Combo Level</p>
                      <p className="text-[10px] text-rose-600">Weight: +10 to -8 pts</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs text-gray-600">
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <p className="text-[10px] uppercase text-gray-400 mb-1">Scope</p>
                      <p>Evaluates <strong>all 5 ball positions together</strong> in one ticket. Counts how many positions follow the favorable reversal pattern vs how many continue unfavorably.</p>
                    </div>
                    <div className="bg-rose-50 rounded-lg p-2.5 border border-rose-100">
                      <p className="text-[10px] uppercase text-rose-400 mb-1">Scoring</p>
                      <p><code className="text-rose-700">≥3 favorable reversals: +10</code></p>
                      <p><code className="text-rose-700">≥2 favorable: +6</code></p>
                      <p><code className="text-rose-700">≥3 unfavorable continuations: -8</code></p>
                      <p><code className="text-rose-700">≥2 unfavorable: -4</code></p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2.5 border border-amber-100">
                      <p className="text-[10px] uppercase text-amber-500 mb-1">Used in</p>
                      <p>C1/C2/C3 Combo Generation, W28 Validator — filters full ticket combinations.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-indigo-50 border-t border-indigo-200 px-4 py-2.5">
                <p className="text-[11px] text-indigo-700"><strong>Key difference:</strong> W11 decides <em>which diff</em> to predict for one ball. C20 decides <em>whether a full 5-ball ticket</em> has enough reversals across all positions. A ticket can have Ball 1 following W11's reversal perfectly but fail C20 if Balls 2-5 all continue unfavorably.</p>
              </div>
            </div>

            {/* W12 vs C21: Position Range */}
            <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid="crossover-w12-c21">
              <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
                <h3 className="font-bold text-sm text-amber-800">Position Range — W12 vs C21</h3>
                <p className="text-[11px] text-amber-600 mt-0.5">Each ball position has a historical p10-p90 range; values outside are penalized</p>
              </div>
              <div className="grid md:grid-cols-2 divide-x divide-gray-200">
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-xs font-black text-white">W12</div>
                    <div>
                      <p className="font-bold text-sm text-gray-900">Per-Ball Level</p>
                      <p className="text-[10px] text-amber-600">Weight: 10% of total score</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs text-gray-600">
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <p className="text-[10px] uppercase text-gray-400 mb-1">Scope</p>
                      <p>Evaluates <strong>one predicted value</strong> for one ball position. Checks if (last_value + diff) falls within that position's p10-p90 range.</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2.5 border border-amber-100">
                      <p className="text-[10px] uppercase text-amber-400 mb-1">Ranges & Scoring</p>
                      <div className="grid grid-cols-2 gap-1 mt-1">
                        <span className="bg-white rounded px-1.5 py-0.5 text-center border">B1: 2-18</span>
                        <span className="bg-white rounded px-1.5 py-0.5 text-center border">B2: 7-28</span>
                        <span className="bg-white rounded px-1.5 py-0.5 text-center border">B3: 13-37</span>
                        <span className="bg-white rounded px-1.5 py-0.5 text-center border">B4: 22-44</span>
                        <span className="bg-white rounded px-1.5 py-0.5 text-center border col-span-2">B5: 33-50</span>
                      </div>
                      <p className="mt-1.5"><code className="text-amber-700">Outside: -6</code> &nbsp; <code className="text-amber-700">Inside: 0 to +3 (graduated by proximity to center)</code></p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2.5 border border-amber-100">
                      <p className="text-[10px] uppercase text-amber-500 mb-1">Used in</p>
                      <p>Window 3 "Most Deserving Difference" — penalizes diffs that would push a ball outside its natural range.</p>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-xs font-black text-gray-600 border border-gray-200">C21</div>
                    <div>
                      <p className="font-bold text-sm text-gray-900">Combo Level</p>
                      <p className="text-[10px] text-blue-600">Weight: +6 to -6 pts</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs text-gray-600">
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <p className="text-[10px] uppercase text-gray-400 mb-1">Scope</p>
                      <p>Evaluates <strong>all 5 balls simultaneously</strong> in one ticket. Counts how many of the 5 balls fall within their respective position ranges.</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-2.5 border border-blue-100">
                      <p className="text-[10px] uppercase text-blue-400 mb-1">Scoring</p>
                      <p><code className="text-blue-700">All 5 in range: +6</code></p>
                      <p><code className="text-blue-700">1 out of range: +3 (warning)</code></p>
                      <p><code className="text-blue-700">2+ out of range: -6 (penalty)</code></p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2.5 border border-amber-100">
                      <p className="text-[10px] uppercase text-amber-500 mb-1">Used in</p>
                      <p>C1/C2/C3 Combo Generation, W28 Validator — rejects tickets with structurally impossible ball placements.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-indigo-50 border-t border-indigo-200 px-4 py-2.5">
                <p className="text-[11px] text-indigo-700"><strong>Key difference:</strong> W12 uses a <em>graduated</em> score (higher near the center of the range) to rank individual diffs. C21 uses a <em>binary count</em> (how many of 5 balls are in/out) to accept or reject an entire ticket. A ticket could have 4 balls perfectly centered but fail C21 if the 5th ball is way out of range.</p>
              </div>
            </div>

            {/* C18/C19 vs W13 Markov */}
            <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid="crossover-w13-c18">
              <div className="bg-cyan-50 border-b border-cyan-200 px-4 py-3">
                <h3 className="font-bold text-sm text-cyan-800">Diff Analysis — W13 Markov vs C18/C19 Diff Guards</h3>
                <p className="text-[11px] text-cyan-600 mt-0.5">Both analyze position-by-position differences but at different granularity</p>
              </div>
              <div className="grid md:grid-cols-2 divide-x divide-gray-200">
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-700 flex items-center justify-center text-xs font-black text-white">W13</div>
                    <div>
                      <p className="font-bold text-sm text-gray-900">Per-Ball Level</p>
                      <p className="text-[10px] text-cyan-600">Weight: 2% of total score</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs text-gray-600">
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <p className="text-[10px] uppercase text-gray-400 mb-1">What it does</p>
                      <p>For <strong>one ball</strong>, looks at Markov transitions: "historically, after diff=+3, what diffs came next?" Boosts diffs that commonly follow, penalizes diffs that never followed.</p>
                    </div>
                    <div className="bg-cyan-50 rounded-lg p-2.5 border border-cyan-100">
                      <p className="text-[10px] uppercase text-cyan-400 mb-1">Scoring</p>
                      <p><code className="text-cyan-700">Top Markov: +8</code> &nbsp; <code className="text-cyan-700">Strong: +5</code></p>
                      <p><code className="text-cyan-700">Moderate: +2</code> &nbsp; <code className="text-cyan-700">Never followed: -4</code></p>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex gap-1">
                      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-[10px] font-black text-gray-600 border border-gray-200">C18</div>
                      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-[10px] font-black text-gray-600 border border-gray-200">C19</div>
                    </div>
                    <div>
                      <p className="font-bold text-sm text-gray-900">Combo Level</p>
                      <p className="text-[10px] text-rose-600">Weight: bonus only (+4, +8)</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs text-gray-600">
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <p className="text-[10px] uppercase text-gray-400 mb-1">What they do</p>
                      <p><strong>C18:</strong> Checks if <em>any</em> of the 5 positions would repeat the exact same diff as last draw. <strong>C19:</strong> Checks if adjacent positions (B1-B2, B2-B3...) share the same direction.</p>
                    </div>
                    <div className="bg-rose-50 rounded-lg p-2.5 border border-rose-100">
                      <p className="text-[10px] uppercase text-rose-400 mb-1">Scoring (bonus only)</p>
                      <p><code className="text-rose-700">C18: 0 same diffs: +4, ≤1 same dir: +3</code></p>
                      <p><code className="text-rose-700">C19: ≥3 adj same dir: +8, ≥2: +5</code></p>
                      <p className="text-[10px] mt-1 text-gray-400">Penalties removed after backtest (+13% coverage)</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-indigo-50 border-t border-indigo-200 px-4 py-2.5">
                <p className="text-[11px] text-indigo-700"><strong>Key difference:</strong> W13 uses <em>Markov chain history</em> to rank individual diffs ("what typically follows?"). C18/C19 use <em>structural checks</em> to filter full tickets ("does this ticket have unusual diff patterns?"). W13 is predictive; C18/C19 are guardrails.</p>
              </div>
            </div>
          </div>
        )}

        {/* Diversity Engine */}
        {activeTab === 'diversity' && (
          <div className="space-y-4" data-testid="diversity-section">
            <h2 className="text-base font-bold text-gray-900">Diversity Engine — Backtest-Validated</h2>
            <p className="text-xs text-gray-500">
              After scoring, tickets are selected using diversity enforcement to maximize number coverage across the final set.
              Without diversity, all 10 tickets converge on the same ~14 numbers. With it, they cover 18-19 unique numbers.
            </p>

            {DIVERSITY_PRINCIPLES.map((d, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`diversity-${i}`}>
                <h3 className="font-bold text-sm text-emerald-700 mb-2">{d.name}</h3>
                <p className="text-xs text-gray-600 leading-relaxed mb-2">{d.detail}</p>
                <div className="flex items-center gap-2 bg-emerald-50 rounded-lg px-3 py-1.5 border border-emerald-200 w-fit">
                  <TrendUp size={14} className="text-emerald-600" />
                  <span className="text-xs text-emerald-700 font-medium">{d.impact}</span>
                </div>
              </div>
            ))}

            {/* Backtest results summary */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5">
              <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-emerald-800">
                <ChartBar size={16} className="text-emerald-600" />
                Backtest Results (20 draws, 3 trials averaged)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-400 border-b border-emerald-200">
                      <th className="py-2 text-left font-medium">Metric</th>
                      <th className="py-2 text-right font-medium">Before</th>
                      <th className="py-2 text-right font-medium">After</th>
                      <th className="py-2 text-right font-medium">Change</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-emerald-100">
                      <td className="py-2.5">Unique numbers (10 tickets)</td>
                      <td className="py-2.5 text-right text-red-500 font-medium">14</td>
                      <td className="py-2.5 text-right text-emerald-600 font-medium">18-19</td>
                      <td className="py-2.5 text-right text-emerald-600 font-bold">+29%</td>
                    </tr>
                    <tr className="border-b border-emerald-100">
                      <td className="py-2.5">Candidate pool (per position)</td>
                      <td className="py-2.5 text-right text-red-500 font-medium">15</td>
                      <td className="py-2.5 text-right text-emerald-600 font-medium">25</td>
                      <td className="py-2.5 text-right text-emerald-600 font-bold">+67%</td>
                    </tr>
                    <tr>
                      <td className="py-2.5">API response time</td>
                      <td className="py-2.5 text-right text-gray-400">~2s</td>
                      <td className="py-2.5 text-right text-emerald-600 font-medium">~1.3s</td>
                      <td className="py-2.5 text-right text-emerald-600 font-bold">-35%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
